import { Inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DOCUMENT } from '@angular/common';

import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';

import { TmtLoggerService } from 'tmt-logger';

import { environment } from '../../environments/environment';
import { UserData } from '../models/user-data';

/**
 * Service handling tasks related to user authorization.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
	/**
	 *
	 * @param router
	 * @param route
	 * @param loggerService
	 * @param document
	 */
	constructor(
		private router: Router,
		private route: ActivatedRoute,
		private loggerService: TmtLoggerService,
		@Inject(DOCUMENT) private document: Document,
	) {}

	private userDataSubject: BehaviorSubject<UserData> = new BehaviorSubject({
		sessionID: localStorage.getItem('sessionId'),
		userName: localStorage.getItem('userName'),
		isLoggedIn: !!localStorage.getItem('sessionId'),
		companyName: localStorage.getItem('companyName'),
	} as UserData);

	public userData$ = this.userDataSubject.asObservable();

	public isLoggedIn$ = this.userData$.pipe(map(userData => !!userData.sessionID || !!localStorage.getItem('sessionId')));

	public startedRedirect = false;

	public signedOut = false;

	public sessionID$ = this.userData$.pipe(map(userData => userData.sessionID ?? localStorage.getItem('sessionId')));

	/**
	 * Clears current session.
	 */
	public clearSession(redirectToRoot = false): void {
		this.signedOut = true;
		this.userDataSubject.next({ userName: '', sessionID: '', isLoggedIn: false, companyName: '' });

		localStorage.clear();

		if (redirectToRoot) {
			this.router.navigateByUrl('/');
		}
	}

	/**
	 * Updates session with new data.
	 * @param sessionId
	 * @param username
	 * @param company
	 */
	public updateSession(sessionId: string, username: string, company: string) {
		const existingSessionId: string = localStorage.getItem('sessionId') ?? '';
		const existingUsername: string = localStorage.getItem('userName') ?? '';
		const existingCompany: string = localStorage.getItem('companyName') ?? '';

		if (existingCompany !== company || existingUsername !== username || existingSessionId !== sessionId) {
			localStorage.setItem('sessionId', sessionId);
			localStorage.setItem('userName', username);
			localStorage.setItem('companyName', company);

			this.userDataSubject.next({
				sessionID: sessionId,
				userName: username,
				companyName: company,
				isLoggedIn: true,
			} as UserData);
		} else {
			this.removeSpecificQueryParams(['sessionId', 'username', 'company', 'fenixOneTimeToken']);
		}
	}

	/**
	 * Performs login.
	 */
	public login() {
		const host = encodeURIComponent(window.location.href);
		const loginUrl = `${environment.colaConfig?.redirectUrl}?origin=${host}`;
		this.loggerService.flush();
		document.location.href = loginUrl;
	}

	/**
	 * Removes input query params from URL, to handle removing sessionID etc.
	 * @param paramsToRemove
	 */
	public removeSpecificQueryParams(paramsToRemove: string[]) {
		// Get the current query parameters
		const queryParams = { ...this.route.snapshot.queryParams };

		// Remove specific parameters
		paramsToRemove.forEach(param => delete queryParams[param]);

		// Construct a new URL without the removed parameters
		const urlTree = this.router.createUrlTree([], { queryParams });

		// Navigate to the new URL
		this.router.navigateByUrl(urlTree);
	}
}
